import { service } from '@ember/service'
import Component from '@glimmer/component'
import { isEmpty } from '@ember/utils'
import { format } from 'date-fns'
import { colors } from '@colors'
import convertDateFormat from 'teacher-ui-client/utils/giraffe/convert-date-format'
import lexileYRange from 'teacher-ui-client/utils/giraffe/lexile-y-range'
import {
  gradePositionRangeFromLexiles,
  lexileRangeFromGradePosition,
} from 'teacher-ui-client/utils/giraffe/lexile-grade-map'
import { reportingPlotLine as plotLine } from 'teacher-ui-client/utils/giraffe/highcharts-config'
import { t, type IntlService } from 'ember-intl'
import type { Point, SeriesLineOptions, AxisLabelsFormatterContextObject } from 'highcharts'
import { LeftSplitChart } from 'teacher-ui-client/components/giraffe/left-split-chart'
import { SummaryBox } from 'teacher-ui-client/components/giraffe/summary-box'
import { SeriesLine } from 'teacher-ui-client/components/giraffe/primitives/series-line'
import { SummaryBoxColorScheme } from 'teacher-ui-client/components/giraffe/summary-box/themes'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'

export interface LexileDetailsReportData {
  summary: {
    lexile_level: number
    lexile_growth: number
  }
  months: {
    month: string
    lexile: Nullable<number>
  }[]
}

interface Args {
  data: LexileDetailsReportData
  product: 're' | 'rex'
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

export class LexileDetailsStudent extends Component<Signature> {
  @service intl!: IntlService
  @service gradeSets!: GradeSetsService

  readonly chartMargin = [null, 110, null, null]

  readonly colorScheme = SummaryBoxColorScheme.LightBlue

  get summaryStats() {
    const lexileGrowth = this.args.data.summary.lexile_growth
    return [{ label: 'Lexile Growth', value: `${lexileGrowth}` }]
  }

  get lexileEventsWithGapsFilled() {
    const { months } = this.args.data

    const currentMonth = format(new Date(), this.intl.t('dateFormats.dateFns.yyyy-MM'))
    const gradeMonthData = months.find((event) => event.month === currentMonth) || months[months.length - 1]
    const currentMonthIndex = months.indexOf(gradeMonthData)

    const events = months.map((row, index) => {
      if (index === 0) return row

      if (index <= currentMonthIndex && !row.lexile) {
        const previousRow = months[index - 1]
        row.lexile = previousRow.lexile
      }

      return row
    })
    return events
  }

  get lexiles() {
    return this.lexileEventsWithGapsFilled.map((event) => event.lexile)
  }

  get lexileChartData() {
    return this.lexileEventsWithGapsFilled.map((lexileEvent, index, lexiles) => {
      const x = index
      const y = lexileEvent.lexile

      const currentLexile = lexileEvent.lexile
      const previousLexile = lexiles[index - 1]?.lexile
      const hasLexile = typeof previousLexile === 'number' && typeof currentLexile === 'number'
      const lexileGrowth = hasLexile ? currentLexile - previousLexile : 0

      return { x, y, lexileGrowth }
    })
  }

  get categories() {
    return this.lexileEventsWithGapsFilled.map((event) => event.month)
  }

  get chartData(): SeriesLineOptions[] {
    const data = this.lexileChartData

    return [{ data, color: colors.oceanyBlue300, type: 'line' }]
  }

  xAxisLabelFormatter(this: AxisLabelsFormatterContextObject) {
    return typeof this.value === 'string' ? convertDateFormat(this.value) : this.value.toString()
  }

  get toolTipFormatter() {
    return function (this: Point) {
      const { y: lexile, lexileGrowth } = this as Point & { lexileGrowth: number }
      const sign = lexileGrowth < 0 ? '' : '+' // negative sign part of number
      return `${lexile}L </br> ${sign}${lexileGrowth} Growth`
    }
  }

  get plotLines() {
    const product = this.args.product
    const { lexiles } = this
    const positionPadding = 1 // add extra plotline to chart
    const gradePositionRange = gradePositionRangeFromLexiles(product, lexiles, positionPadding)

    const plotLines = gradePositionRange.map((gradePosition) => {
      const { min: lexile } = lexileRangeFromGradePosition(product, gradePosition) as { min: number; max: number }
      const label = this.gradeSets.fullNameFromGradeSetByPosition(gradePosition) ?? ''

      if (isEmpty(lexile)) {
        return null
      }

      return plotLine(lexile, label)
    })

    return plotLines.filter((line) => !isEmpty(line))
  }

  get yRange() {
    const [min, max] = lexileYRange(this.args.product, this.lexiles)
    return [min, max]
  }

  <template>
    <div data-test-giraffe-lexile-details-student class="h-full" ...attributes>
      <LeftSplitChart>
        <:left>
          <SummaryBox
            class="h-full"
            @title={{t "reporting.summaryTitles.lexileDetailsStudent"}}
            @stats={{this.summaryStats}}
            @colorScheme={{this.colorScheme}}
          />
        </:left>
        <:right>
          <SeriesLine
            @data={{this.chartData}}
            @categories={{this.categories}}
            @plotLines={{this.plotLines}}
            @chartMargin={{this.chartMargin}}
            @xAxisLabelFormatter={{this.xAxisLabelFormatter}}
            @toolTipFormatter={{this.toolTipFormatter}}
            @yAxisLabel={{t "reporting.yAxisLabels.lexileDetailsStudent"}}
            @yRange={{this.yRange}}
          />
        </:right>
      </LeftSplitChart>
    </div>
  </template>
}

export default LexileDetailsStudent
